import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { SortableColumnPicker } from "@/components/dataTable";
import type { HuelleColumnSettings, HuelleColumnView } from "@/lib/wartungen";
import {
  HUELLE_REQUIRED_COLUMNS,
  getAvailableHuelleColumns,
  getDefaultHuelleColumns,
  getHuelleColumnLabel,
  normalizeHuelleColumnSelection,
  type HuelleColumnLabelMap,
} from "./columns";

export interface HuelleColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  settings: HuelleColumnSettings;
  masterdataColumns?: string[];
  columnLabels?: HuelleColumnLabelMap;
  onSave: (next: HuelleColumnSettings) => Promise<void> | void;
}

type View = "desktop" | "mobile";

export function HuelleColumnsDialog({
  open,
  onClose,
  settings,
  masterdataColumns = [],
  columnLabels = {},
  onSave,
}: HuelleColumnsDialogProps) {
  const available = useMemo(
    () => getAvailableHuelleColumns(masterdataColumns),
    [masterdataColumns],
  );
  const defaults = useMemo(() => getDefaultHuelleColumns(masterdataColumns), [masterdataColumns]);
  const [tab, setTab] = useState<View>("desktop");
  const [desktop, setDesktop] = useState<string[]>(() =>
    normalizeHuelleColumnSelection(settings.desktop?.columns, available),
  );
  const [mobile, setMobile] = useState<string[]>(() =>
    normalizeHuelleColumnSelection(settings.mobile?.columns, available),
  );
  const [saving, setSaving] = useState(false);
  const [baselineDesktop, setBaselineDesktop] = useState<string[]>(desktop);
  const [baselineMobile, setBaselineMobile] = useState<string[]>(mobile);

  useEffect(() => {
    if (!open) return;
    const nextDesktop =
      settings.desktop?.columns?.length
        ? normalizeHuelleColumnSelection(settings.desktop.columns, available)
        : defaults;
    const nextMobile =
      settings.mobile?.columns?.length
        ? normalizeHuelleColumnSelection(settings.mobile.columns, available)
        : defaults;
    setDesktop(nextDesktop);
    setMobile(nextMobile);
    setBaselineDesktop(nextDesktop);
    setBaselineMobile(nextMobile);
  }, [open, settings, available, defaults]);

  const current = tab === "desktop" ? desktop : mobile;
  const setCurrent = tab === "desktop" ? setDesktop : setMobile;

  async function handleSave() {
    setSaving(true);
    try {
      const desktopView: HuelleColumnView = {
        columns: normalizeHuelleColumnSelection(desktop, available),
        widths: settings.desktop?.widths ?? {},
      };
      const mobileView: HuelleColumnView = {
        columns: normalizeHuelleColumnSelection(mobile, available),
        widths: settings.mobile?.widths ?? {},
      };
      await onSave({ desktop: desktopView, mobile: mobileView });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const dirty = useMemo(
    () =>
      hasFormChanges(desktop, baselineDesktop) || hasFormChanges(mobile, baselineMobile),
    [desktop, baselineDesktop, mobile, baselineMobile],
  );

  const requestClose = useDialogRequestClose(onClose);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Spalten anpassen"
      description="Wähle die Spalten für die Objektliste und ziehe sie per Drag-and-Drop in die gewünschte Reihenfolge."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => setCurrent(defaults)} disabled={saving}>
            Standard wiederherstellen
          </Button>
          <Button variant="secondary" onClick={() => setCurrent(available)} disabled={saving}>
            Alle auswählen
          </Button>
          <Button variant="ghost" onClick={requestClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSave()} loading={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="mb-3 flex gap-1 rounded-full bg-fill-tertiary p-1 text-sm">
        <TabButton active={tab === "desktop"} onClick={() => setTab("desktop")}>
          Desktop
        </TabButton>
        <TabButton active={tab === "mobile"} onClick={() => setTab("mobile")}>
          Mobil
        </TabButton>
      </div>

      <SortableColumnPicker
        available={available}
        selected={current}
        requiredColumns={[...HUELLE_REQUIRED_COLUMNS]}
        getLabel={(col) => getHuelleColumnLabel(col, columnLabels)}
        onSelectedChange={setCurrent}
        disabled={saving}
      />
    </Dialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex-1 rounded-full px-3 py-1.5 font-medium transition",
        active ? "bg-card text-label shadow-sm" : "text-label-secondary hover:text-label",
      )}
    >
      {children}
    </button>
  );
}
